import { useCartAndDeliveryDatesDataContext } from '@app/data/clients/cartCheckoutClient';
import { Item } from '@redbubble/next-analytics/gtm';
import { useCallback } from 'react';
import type { PayPalExpressProps } from './PayPalExpress';
import { PayPalExpress } from './PayPalExpress';
import { intlErrorMessages as PayPalMessages } from './paymentErrorLookup';
import type { ErrorMessage, PageType, PayPalErrorCallback } from './types';
import { usePayPalShippingAddressChangeHandler } from './usePayPalShippingAddressChangeHandler';
import { usePayPalShippingPreferenceChangeHandler } from './usePayPalShippingPreferenceChangeHandler';
import {
  mapCartToPayPalRequiredCartFromMonolith,
  onPayPalApproveOrderError,
  onPayPalCreateOrderError,
  onPayPalInitError,
} from './utils';

export interface MonolithCartPaypalExpressContainerProps {
  selectorId: string;
  page: PageType;
  metricName: string;
  onError: (message: ErrorMessage) => void;
  email?: string | null;
  shippingAddressEditable?: boolean;
  shippingOptionsEditable?: boolean;
  onPayError?: PayPalErrorCallback;
  getAddressOverride?: PayPalExpressProps['getAddressOverride'];
  onStart?: () => void;
  onCancel?: () => Promise<void>;
  analyticsItems?: (Item & { coupon: string })[];
}

export const MonolithCartPaypalExpressContainer = ({
  selectorId,
  page,
  metricName,
  onError,
  email,
  shippingAddressEditable,
  shippingOptionsEditable,
  onPayError,
  getAddressOverride,
  onStart,
  onCancel,
  analyticsItems,
}: MonolithCartPaypalExpressContainerProps) => {
  const { cart } = useCartAndDeliveryDatesDataContext();

  const { handleShippingAddressChange } =
    usePayPalShippingAddressChangeHandler();
  const { handleShippingPreferenceChange } =
    usePayPalShippingPreferenceChangeHandler();

  const handleInitError: PayPalErrorCallback = useCallback(
    (message) => onPayPalInitError({ message, onError, metricName }),
    [onError, metricName]
  );

  const handleCreateOrderError: PayPalErrorCallback = useCallback(
    (message) => onPayPalCreateOrderError({ message, onError, metricName }),
    [onError, metricName]
  );

  const handleApproveOrderError: PayPalErrorCallback = useCallback(
    (message) => onPayPalApproveOrderError({ message, onError, metricName }),
    [onError, metricName]
  );

  const handlePayError: PayPalErrorCallback = useCallback(
    (message) => {
      if (onPayError) {
        onPayError(message);
      } else {
        onError(message || PayPalMessages.serverError);
      }
    },
    [onError, onPayError]
  );

  if (!cart) return null;

  const payPalRequiredCart = mapCartToPayPalRequiredCartFromMonolith(cart);

  return (
    <PayPalExpress
      cart={payPalRequiredCart}
      selectorId={selectorId}
      page={page}
      email={email ?? null}
      onInitError={handleInitError}
      onCreateOrderError={handleCreateOrderError}
      onApproveOrderError={handleApproveOrderError}
      onPayError={handlePayError}
      shippingAddressEditable={shippingAddressEditable}
      shippingOptionsEditable={shippingOptionsEditable}
      getAddressOverride={getAddressOverride}
      onStart={onStart}
      onCancel={onCancel}
      onShippingAddressChange={handleShippingAddressChange}
      onShippingPreferenceChange={handleShippingPreferenceChange}
      analyticsItems={analyticsItems}
    />
  );
};
